import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartData, ChartOptions, ChartType, registerables } from 'chart.js';

Chart.register(...registerables);

// Single shared Chart.js wrapper for the whole analytics panel - every chart on every screen
// goes through this component instead of each screen managing its own Chart.js instance.
//
// Every screen passes data/options through a getter (e.g. `get trendChartData()`), so Angular
// hands this component a brand-new object/array identity on every single change-detection cycle,
// not only when a real refetch happens. A reference-based ngOnChanges therefore fired constantly;
// destroying and recreating the whole Chart.js instance (canvas teardown, listener rebinding,
// replaying the entrance animation) on every CD tick was the actual cost, not the data itself
// changing often. Fixed by comparing SERIALIZED content instead of object identity: a getter that
// returns equal values under a new reference is now a no-op, and a real change updates the
// existing chart in place (chart.data=/Object.assign(chart.options,...) + update('none'), no
// re-animation) instead of destroy+create. A `type` change still goes through destroy/create -
// Chart.js doesn't support swapping a live chart's type.
@Component({
	selector: 'app-chart',
	standalone: true,
	imports: [],
	templateUrl: './chart.component.html',
	styleUrl: './chart.component.scss',
})
export class ChartComponent implements AfterViewInit, OnChanges, OnDestroy {
	@Input({ required: true }) type!: ChartType;
	@Input({ required: true }) data!: ChartData;
	@Input() options?: ChartOptions;
	@Input() height = '256px';

	@ViewChild('canvasRef', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

	private chart?: Chart;
	// JSON snapshots of the data/options last actually applied to the live chart. Functions inside
	// `options` (tooltip/axis callbacks, per-point color callbacks) are dropped silently by
	// JSON.stringify, which is fine here - they're derived purely from the data values this
	// snapshot does capture, so a real visual change always shows up as a data difference too.
	private lastDataJson = '';
	private lastOptionsJson = '';

	ngAfterViewInit(): void {
		this.createChart();
		this.lastDataJson = this.toJson(this.data);
		this.lastOptionsJson = this.toJson(this.options);
	}

	ngOnChanges(changes: SimpleChanges): void {
		// First binding is handled by ngAfterViewInit once the <canvas> actually exists -
		// ngOnChanges fires before that on initial load, when there's nothing to update yet.
		if (!this.chart) return;

		if (changes['type'] && changes['type'].previousValue !== changes['type'].currentValue) {
			this.createChart();
			this.lastDataJson = this.toJson(this.data);
			this.lastOptionsJson = this.toJson(this.options);
			return;
		}

		const dataJson = this.toJson(this.data);
		const optionsJson = this.toJson(this.options);
		if (dataJson === this.lastDataJson && optionsJson === this.lastOptionsJson) {
			return;
		}
		this.lastDataJson = dataJson;
		this.lastOptionsJson = optionsJson;

		this.chart.data = this.data;
		if (this.options) {
			Object.assign(this.chart.options, this.options);
		}
		this.chart.update('none');
	}

	ngOnDestroy(): void {
		this.chart?.destroy();
	}

	private toJson(value: unknown): string {
		try {
			return JSON.stringify(value) ?? '';
		} catch {
			return '';
		}
	}

	private createChart(): void {
		this.chart?.destroy();
		this.chart = new Chart(this.canvasRef.nativeElement, {
			type: this.type,
			data: this.data,
			options: this.options,
		} as ChartConfiguration);
	}
}
